import { useState } from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    Typography,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useGetMarketplaceChannels } from '../../../../client/hooks/marketplace/marketplace.queries';

const FreteGratisFilters = ({ filters, onFiltersChange, onClearFilters }) => {
    const [localFilters, setLocalFilters] = useState(filters);
    
    const { data: channelsData } = useGetMarketplaceChannels({}, true);
    
    const getAccountIds = () => {
        if (!channelsData?.data) return [];
        
        const mercadoLivreChannel = channelsData.data.find(channel => 
            channel.channel === 'MERCADO_LIVRE'
        );
        
        return mercadoLivreChannel?.accounts_id || [];
    };
    
    const accountIds = getAccountIds();

    const handleFilterChange = (field, value) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            item_mlb: '',
            list_cost_min: '',
            list_cost_max: '',
            createdAt_start: '',
            createdAt_end: '',
            user_id: ''
        };
        setLocalFilters(clearedFilters);
        onClearFilters();
    };


    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                p: 2.5,
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                mb: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 0 } }}>
                <FilterListIcon sx={{ color: '#ff9800' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                    Filtros
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flex: 1 }}>
                <TextField
                    label="Item MLB"
                    placeholder="Ex: MLB1234567890"
                    value={localFilters.item_mlb || ''}
                    onChange={(e) => handleFilterChange('item_mlb', e.target.value)}
                    size="small"
                    sx={{ minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#ff9800' }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Tooltip title="ID da conta do Mercado Livre" arrow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Account ID</InputLabel>
                        <Select
                            value={localFilters.user_id || ''}
                            label="Account ID"
                            onChange={(e) => handleFilterChange('user_id', e.target.value)}
                        >
                        <MenuItem value="">
                            <em>Todos</em>
                        </MenuItem>
                        {accountIds.map((accountId) => (
                            <MenuItem key={accountId} value={accountId}>
                                {accountId}
                            </MenuItem>
                        ))}
                        </Select>
                    </FormControl>
                </Tooltip>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ minWidth: 'fit-content', color: '#666' }}>
                        Custo:
                    </Typography>
                    <TextField
                        label="Valor mínimo"
                        type="number"
                        value={localFilters.list_cost_min || ''}
                        onChange={(e) => handleFilterChange('list_cost_min', e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>
                        }}
                    />
                    <Typography variant="body2" sx={{ color: '#666' }}>até</Typography>
                    <TextField
                        label="Valor máximo"
                        type="number"
                        value={localFilters.list_cost_max || ''}
                        onChange={(e) => handleFilterChange('list_cost_max', e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ minWidth: 'fit-content', color: '#666' }}>
                        Período:
                    </Typography>
                    <TextField
                        label="Data inicial"
                        type="date"
                        value={localFilters.createdAt_start || ''}
                        onChange={(e) => handleFilterChange('createdAt_start', e.target.value)}
                        size="small"
                        sx={{ width: 140 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Typography variant="body2" sx={{ color: '#666' }}>até</Typography>
                    <TextField
                        label="Data final"
                        type="date"
                        value={localFilters.createdAt_end || ''}
                        onChange={(e) => handleFilterChange('createdAt_end', e.target.value)}
                        size="small"
                        sx={{ width: 140 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Limpar filtros">
                    <IconButton
                        onClick={handleClearFilters}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                bgcolor: '#fff3e0',
                                color: '#ff9800'
                            }
                        }}
                    >
                        <ClearIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default FreteGratisFilters;